import { Component, type ReactNode, type ErrorInfo } from 'react'
import { LuTriangleAlert, LuRefreshCw } from 'react-icons/lu'
import { Button, GlassCard } from '@components/ui'

interface Props {
  children: ReactNode
  fallback?: ReactNode
}

interface State {
  hasError: boolean
  error?: Error
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props)
    this.state = { hasError: false }
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error }
  }

  componentDidCatch(_error: Error, _errorInfo: ErrorInfo) {

    // TODO: Send to error tracking service (e.g., Sentry)
  }

  handleReload = () => {
    window.location.reload()
  }

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback
      }

      return (
        <div className="min-h-screen flex items-center justify-center bg-[var(--color-bg-primary)] p-6">
          <GlassCard className="p-8 max-w-md text-center">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-red-500/20 flex items-center justify-center">
              <LuTriangleAlert className="w-8 h-8 text-red-500" />
            </div>
            <h2 className="text-xl font-bold text-[var(--color-text-primary)] mb-2">Something went wrong</h2>
            <p className="text-[var(--color-text-secondary)] mb-4">
              An unexpected error occurred. Please try reloading the page.
            </p>
            {this.state.error && (
              <p className="text-xs text-[var(--color-text-muted)] mb-4 font-mono bg-[var(--color-surface)] p-2 rounded">
                {this.state.error.message}
              </p>
            )}
            <Button onClick={this.handleReload}>
              <LuRefreshCw className="w-4 h-4 mr-2" />
              Reload Page
            </Button>
          </GlassCard>
        </div>
      )
    }

    return this.props.children
  }
}
